import { queryOptions } from '@tanstack/react-query';

import { ONE_MINUTE } from '@/constants/time';
import { paths } from '@/types/api';
import { fetchClient } from '@/util/fetchClient';

export type PublicExpertProfileV2 = NonNullable<
  paths['/v2/public/experts/{slug}']['get']['responses'][200]['content']['application/json']['data']
>;

/**
 * The public card behind an expert's personal link. Resolves a current or
 * retired slug; an unknown slug and an expert who may not be shown both throw
 * `ApiError` from `fetchClient`, because the API answers them identically
 * with a non-2xx response. The guard below is a defensive fallback for a 200
 * response with an unexpectedly empty body, not the path unknown slugs take.
 */
export async function getPublicExpertProfileV2(slug: string): Promise<PublicExpertProfileV2> {
  const { data } = await fetchClient.GET('/v2/public/experts/{slug}', {
    params: { path: { slug } },
  });

  if (!data?.data) {
    throw new Error('Expert link not found');
  }

  return data.data;
}

export const getPublicExpertProfileV2QueryOptions = (slug?: string) =>
  queryOptions<PublicExpertProfileV2>({
    queryKey: ['publicExpertProfileV2', slug],
    queryFn: () => getPublicExpertProfileV2(slug ?? ''),
    staleTime: ONE_MINUTE,
    enabled: !!slug,
    retry: false,
  });
